import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Infinity as InfinityIcon, Files, Share2, Gauge, Navigation, CloudUpload, FlaskConical, Loader2, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const PERKS = [
  { icon: InfinityIcon, label: "Unlimited receipts (vs 30/mo on Free)" },
  { icon: Files, label: "Year-end audit PDF bundle with every receipt thumbnail" },
  { icon: Share2, label: "Bookkeeper share link — read-only access for your CPA" },
  { icon: Gauge, label: "IFTA quarterly fuel-tax summary (per-state)" },
  { icon: Navigation, label: "Mileage deduction tracker @ $0.67/mile (IRS 2026)" },
  { icon: CloudUpload, label: "Priority cloud backup" },
];

export default function Upgrade() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  const [testMode, setTestMode] = useState(false);
  const [cfg, setCfg] = useState({ monthly_price: 9.99, yearly_price: 79, public_discounts: [] });

  useEffect(() => {
    document.title = "RigBooks Pro · Upgrade";
    api("/payments/config")
      .then((c) => { setTestMode(!!c?.test_mode); if (c) setCfg(c); })
      .catch(() => {});
  }, []);

  const discountFor = (plan) => (cfg.public_discounts || []).find((d) => d.plan === plan) || null;
  const priceFor = (plan) => {
    const base = plan === "monthly" ? cfg.monthly_price : cfg.yearly_price;
    const d = discountFor(plan);
    return d && d.price_usd < base ? d.price_usd : base;
  };
  const fmt = (n) => (Number(n) % 1 === 0 ? `$${Number(n)}` : `$${Number(n).toFixed(2)}`);

  const checkout = async (pkg) => {
    setBusy(pkg);
    try {
      const r = await api("/payments/checkout", {
        method: "POST",
        body: { package_id: pkg, origin_url: window.location.origin },
      });
      window.location.href = r.url;
    } catch (e) {
      toast.error(e?.message || "Checkout failed.");
      setBusy(null);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Button variant="ghost" onClick={() => navigate(-1)} className="text-rig-dim hover:text-white">
        <ArrowLeft className="mr-2 h-4 w-4" /> Back
      </Button>

      {testMode && (
        <div className="flex items-center gap-2 rounded-xl border border-rig-warning/50 bg-rig-warning/10 px-4 py-3">
          <FlaskConical className="h-4 w-4 shrink-0 text-rig-warning" />
          <p className="text-xs font-semibold text-rig-warning" data-testid="test-mode-badge">
            STRIPE TEST MODE · use card 4242 4242 4242 4242, any future date, any CVC
          </p>
        </div>
      )}

      <div>
        <span className="inline-block rounded-full border border-rig-accent bg-rig-accent/15 px-3 py-1 font-display text-xs font-bold uppercase tracking-widest text-rig-accent">
          RigBooks Pro
        </span>
        <h2 className="mt-4 font-display text-4xl font-bold uppercase leading-tight tracking-wide text-white sm:text-5xl">
          Never lose a deduction again.
        </h2>
        <p className="mt-3 max-w-xl text-rig-dim">
          Unlock the tools serious owner-operators use to maximize every legal write-off and survive any audit.
        </p>
      </div>

      <div className="rounded-2xl border border-rig-border bg-rig-surface p-6">
        <div className="grid gap-4 sm:grid-cols-2">
          {PERKS.map((p) => (
            <div key={p.label} className="flex items-start gap-3">
              <p.icon className="mt-0.5 h-5 w-5 shrink-0 text-rig-accent" />
              <span className="text-sm text-white">{p.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <button
          onClick={() => checkout("monthly")}
          disabled={!!busy}
          data-testid="plan-monthly"
          className="rounded-2xl border border-rig-border bg-rig-surface p-6 text-center transition-all hover:border-rig-borderStrong disabled:opacity-60"
        >
          <div className="font-display text-xs font-bold uppercase tracking-widest text-rig-dim">Monthly</div>
          {discountFor("monthly") ? (
            <div className="my-2">
              <span className="mr-2 font-display text-2xl font-bold text-rig-mute line-through">{fmt(cfg.monthly_price)}</span>
              <span className="font-display text-5xl font-bold text-rig-accent" data-testid="monthly-price">{fmt(priceFor("monthly"))}</span>
            </div>
          ) : (
            <div className="my-2 font-display text-5xl font-bold text-white" data-testid="monthly-price">{fmt(cfg.monthly_price)}</div>
          )}
          <div className="text-sm text-rig-dim">per month</div>
          {discountFor("monthly") && (
            <div className="mt-2 inline-block rounded-full bg-rig-accent/15 px-3 py-1 font-display text-[10px] font-bold uppercase tracking-widest text-rig-accent" data-testid="monthly-discount-badge">
              {discountFor("monthly").label || "Discount applied"}
            </div>
          )}
          {busy === "monthly" && <Loader2 className="mx-auto mt-3 h-5 w-5 animate-spin text-rig-accent" />}
        </button>

        <button
          onClick={() => checkout("yearly")}
          disabled={!!busy}
          data-testid="plan-yearly"
          className="relative rounded-2xl border-2 border-rig-accent bg-rig-surface p-6 text-center transition-all hover:bg-rig-surface2 disabled:opacity-60"
        >
          <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-rig-accent px-3 py-1 font-display text-[10px] font-bold uppercase tracking-widest text-rig-accentText">
            Best Value
          </span>
          <div className="font-display text-xs font-bold uppercase tracking-widest text-rig-accent">Yearly</div>
          {discountFor("yearly") ? (
            <div className="my-2">
              <span className="mr-2 font-display text-2xl font-bold text-rig-mute line-through">{fmt(cfg.yearly_price)}</span>
              <span className="font-display text-5xl font-bold text-rig-accent" data-testid="yearly-price">{fmt(priceFor("yearly"))}</span>
            </div>
          ) : (
            <div className="my-2 font-display text-5xl font-bold text-rig-accent" data-testid="yearly-price">{fmt(cfg.yearly_price)}</div>
          )}
          <div className="text-sm text-rig-dim">per year</div>
          {discountFor("yearly") && (
            <div className="mt-2 inline-block rounded-full bg-rig-accent/15 px-3 py-1 font-display text-[10px] font-bold uppercase tracking-widest text-rig-accent" data-testid="yearly-discount-badge">
              {discountFor("yearly").label || "Discount applied"}
            </div>
          )}
          {busy === "yearly" && <Loader2 className="mx-auto mt-3 h-5 w-5 animate-spin text-rig-accent" />}
        </button>
      </div>

      <p className="text-center text-sm text-rig-mute">Pro access begins immediately after payment. Powered by Stripe.</p>
    </div>
  );
}
